import {Component, OnInit} from '@angular/core';
import * as _ from 'lodash';
import {Router} from '@angular/router';
import { User } from 'src/app/_models';
import {AESEncryptDecryptService} from '../../_services/aesencrypt-decrypt.service';
import {Constants} from '../../constants/constants';
import { Location } from '@angular/common';
import {AuthenticationService} from '../../_services';
import {UtilsService} from '../../_services/utils.service';
import {consumeSquareOAuthState} from '../../_services/square-oauth.util';
import {PartnerReauthService} from '../../_services/partner-reauth.service';


@Component({
    selector: 'app-square-auth-router',
    templateUrl: './square-auth-router.component.html',
    styleUrls: ['./square-auth-router.component.css']
})
export class SquareAuthRouterComponent implements OnInit {

    currentUser: User;
    private selectedRole: string;
    errorMessage: string | null = null;

    constructor(private router: Router, private location: Location, private _AESEncryptDecryptService: AESEncryptDecryptService,
                private authenticationService: AuthenticationService, private utilsService: UtilsService) {
        this.currentUser = utilsService.getCurrentUser();
        this.selectedRole = this.currentUser?.selectedRole;
    }


    ngOnInit() {
        // The OAuth handshake just completed (success or denial). Either
        // way we want a future partner-401 to be free to redirect again,
        // so drop the dedupe flag set by PartnerReauthService.
        PartnerReauthService.clearInflight();

        const urlString = window.location.href;
        const url = new URL(urlString);
        const urlCode = url.searchParams.get('code');
        const oauthError = url.searchParams.get('error');

        // Square QA P0: "Selecting Deny on the OAuth permissions screen
        // leads me here: .../squareAuth?error=access_denied..." — we must
        // surface a user-friendly message instead of dumping the query
        // string on an empty page.
        if (oauthError) {
            this.handleOAuthError(oauthError, url.searchParams.get('error_description'));
            return;
        }

        if (!_.isNil(urlCode)) {
            // Square QA P0: validate the CSRF state returned by Square
            // against the one we stored in sessionStorage before we
            // actually trust the code.
            const returnedState = url.searchParams.get('state');
            if (!consumeSquareOAuthState(returnedState)) {
                this.handleOAuthError(
                    'invalid_state',
                    'Security check failed. Please try connecting Square again.'
                );
                return;
            }
            this.initialRouterPropertiesSet(url);
        } else if (url.searchParams.get('user')) {
            this.processLoggedInUrlUser(url);
        } else {
            this.router.navigate(['/login']);
        }
    }

    private handleOAuthError(code: string, description: string | null) {
        // Normalise to a human readable message. We log the raw code for
        // ops but show a friendly string in the UI.
        console.warn('Square OAuth returned error', {code, description});
        if (code === 'access_denied') {
            this.errorMessage = 'You declined to connect Square. You can try again from your account settings.';
        } else if (code === 'invalid_state') {
            this.errorMessage = description || 'Security check failed. Please try connecting Square again.';
        } else {
            this.errorMessage = description || 'Could not complete Square sign-in. Please try again.';
        }
        // Strip the query parameters so a refresh doesn't re-process the
        // error and leak the code / state via browser history.
        this.deleteQueryParameterFromCurrentRoute();
        setTimeout(() => {
            this.router.navigate(['/login']);
        }, 4000);
    }

    processLoggedInUrlUser(url) {
        if (url.searchParams.get('user')) {
            this.currentUser = JSON.parse(this._AESEncryptDecryptService.decrypt(url.searchParams.get('user')));
            if (this.currentUser) {
                localStorage.setItem(Constants.CURRENT_USER, url.searchParams.get('user'));
                this.routeToProperComponent();
            }
        }
    }

    initialRouterPropertiesSet(url) {
        const urlCode = url.searchParams.get('code');

        if (!_.isNil(urlCode)) {
            this.authenticationService.squareLogin(urlCode)
                .subscribe(
                    (user) => {
                        this.currentUser = user;
                        this.selectedRole = this.currentUser.role[0];
                        this.routeToProperComponent();
                    },
                    error => {
                        console.log(error);
                        this.deleteQueryParameterFromCurrentRoute();
                        this.router.navigate(['/login']);
                        alert(error);
                    });
        } else {
            this.deleteQueryParameterFromCurrentRoute();
            this.router.navigate(['/login']);
        }
    }

    private routeToProperComponent() {
        this.deleteQueryParameterFromCurrentRoute();
        if (this.currentUser.role.length > 1) {
            this.router.navigate(['/select-role']);
        } else if ((this.selectedRole === 'Admin'  && !this.currentUser.dashboardDisabled)) {
            this.router.navigate(['/sidebar/admin/dashboard']);
        } else if (this.selectedRole === 'Manager' || this.selectedRole === 'Server' || this.currentUser.dashboardDisabled) {
            this.router.navigate(['sidebar/admin/waiterMenu/serverOrder']).then(() => {
                // window.location.reload();
            });
        } else if (this.selectedRole === 'Table') {
            this.router.navigate(['/sidebar/order']).then(() => {
                // window.location.reload();
            });
        } else if (this.selectedRole === 'PaymentsDisplay') {
            this.router.navigate(['/paymentUser/paymentUser']).then(() => {
                // window.location.reload();
            });
        } else if (this.selectedRole === 'OrdersDisplay') {
            this.router.navigate(['/orderUser/orderUser']).then(() => {
                // window.location.reload();
            });
        } else if (this.selectedRole === 'Kiosk') {
            this.router.navigate(['/kiosk']).then(() => {
                // window.location.reload();
            });
        } else if (this.selectedRole === 'QROrder') {
            this.router.navigate(['/QROrder']).then(() => {
                // window.location.reload();
            });
        } else {
            this.router.navigate(['/login'], {queryParamsHandling: 'preserve'}).then(() => {
                window.location.reload();
            });
        }
    }

    deleteQueryParameterFromCurrentRoute() {
        this.location.replaceState('./squareAuth');
    }
}
